import { getPokemonSlug } from './pokemonDexUrl';

export interface DexBaseStats {
  hp: number;
  atk: number;
  def: number;
  spa: number;
  spd: number;
  spe: number;
}

export interface DexSpecies {
  num: number;
  name: string;
  baseStats: DexBaseStats;
  types: string[];
  abilities: Record<string, string>;
  evos?: string[];
  prevo?: string;
  evoType?: string;
  evoLevel?: number;
  evoItem?: string;
  evoCondition?: string;
  evoMove?: string;
  heightm?: number;
  weightkg?: number;
  eggGroups?: string[];
  tier?: string;
  otherFormes?: string[];
  formeOrder?: string[];
  baseSpecies?: string;
  forme?: string;
}

export interface DexMove {
  num: number;
  name: string;
  basePower: number;
  type: string;
  accuracy: number | boolean;
  pp: number;
  priority: number;
  category: 'Physical' | 'Special' | 'Status';
  desc?: string;
  shortDesc?: string;
}

export interface DexAbility {
  name: string;
  rating?: number;
  num?: number;
  desc?: string;
  shortDesc?: string;
}

export interface DexLearnsetEntry {
  learnset?: Record<string, string[]>;
}

export interface DexDataBundle {
  species: Record<string, DexSpecies>;
  learnsets: Record<string, DexLearnsetEntry>;
  moves: Record<string, DexMove>;
  abilities: Record<string, DexAbility>;
}

export interface EvolutionStep {
  slug: string;
  name: string;
  num: number;
  types: string[];
  depth: number;
  trigger: string;
  isCurrent: boolean;
}

export interface ParsedLearnsetMove {
  slug: string;
  name: string;
  type: string;
  category: 'Physical' | 'Special' | 'Status';
  basePower: number;
  accuracy: number | boolean;
  pp: number;
  desc: string;
  level?: number;
  methodDisplay: string;
}

let dexBundlePromise: Promise<DexDataBundle> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }

    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      if (existing.getAttribute('data-loaded') === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', (e) => reject(e));
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      resolve();
    };
    script.onerror = (e) => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  });
}

/**
 * Loads the ComprehensiveDex data asynchronously from /ComprehensiveDex/data/*.
 * Caches the parsed data bundle in memory so subsequent calls are instant.
 */
export async function loadDexData(): Promise<DexDataBundle> {
  if (dexBundlePromise) {
    return dexBundlePromise;
  }

  dexBundlePromise = (async () => {
    if (typeof window !== 'undefined') {
      if (!(window as any).exports) {
        (window as any).exports = window;
      }

      // Check if already on window
      const win = window as any;
      if (!win.BattlePokedex || !win.BattleLearnsets || !win.BattleMovedex || !win.BattleAbilities) {
        await Promise.all([
          loadScript('/ComprehensiveDex/data/species.js'),
          loadScript('/ComprehensiveDex/data/learnsets.js'),
          loadScript('/ComprehensiveDex/data/moves.js'),
          loadScript('/ComprehensiveDex/data/abilities.js'),
        ]);
      }

      return {
        species: (window as any).BattlePokedex || {},
        learnsets: (window as any).BattleLearnsets || {},
        moves: (window as any).BattleMovedex || {},
        abilities: (window as any).BattleAbilities || {},
      };
    }

    return {
      species: {},
      learnsets: {},
      moves: {},
      abilities: {},
    };
  })();

  return dexBundlePromise;
}

/**
 * Formats the evolution requirement string into human-friendly text.
 */
export function formatEvoTrigger(sp: DexSpecies): string {
  if (sp.evoType === 'levelUp' && sp.evoLevel) return `Lv. ${sp.evoLevel}`;
  if (sp.evoType === 'useItem' && sp.evoItem) return sp.evoItem;
  if (sp.evoType === 'trade') return sp.evoItem ? `Trade + ${sp.evoItem}` : 'Trade';
  if (sp.evoType === 'levelFriendship') {
    return sp.evoCondition ? `Friendship (${sp.evoCondition})` : 'Friendship';
  }
  if (sp.evoType === 'levelMove' && sp.evoMove) return `Knows ${sp.evoMove}`;
  if (sp.evoType === 'levelHold' && sp.evoItem) return `Hold ${sp.evoItem}`;
  if (sp.forme && sp.forme.startsWith('Mega')) return sp.forme;
  if (sp.evoCondition) return sp.evoCondition;
  return sp.evoType || '';
}

/**
 * Traverses upwards to find the base form of the evolutionary family,
 * then traverses down to build the full family tree.
 */
export function buildEvolutionChain(
  currentSlug: string,
  speciesMap: Record<string, DexSpecies>
): EvolutionStep[] {
  const current = speciesMap[currentSlug];
  if (!current) return [];

  // 1. Follow prevo and baseSpecies to reach the root
  let rootSlug = currentSlug;
  let curr = current;
  const visited = new Set<string>([currentSlug]);

  while (curr && curr.prevo) {
    const preSlug = getPokemonSlug(curr.prevo);
    if (visited.has(preSlug) || !speciesMap[preSlug]) break;
    visited.add(preSlug);
    rootSlug = preSlug;
    curr = speciesMap[preSlug];
  }

  // Handle Mega forms pointing to baseSpecies
  if (curr && curr.baseSpecies) {
    const baseSlug = getPokemonSlug(curr.baseSpecies);
    if (speciesMap[baseSlug] && !visited.has(baseSlug)) {
      rootSlug = baseSlug;
      curr = speciesMap[baseSlug];
      while (curr && curr.prevo) {
        const preSlug = getPokemonSlug(curr.prevo);
        if (visited.has(preSlug) || !speciesMap[preSlug]) break;
        visited.add(preSlug);
        rootSlug = preSlug;
        curr = speciesMap[preSlug];
      }
    }
  }

  // 2. Breadth-first traversal down from root
  const chain: EvolutionStep[] = [];
  const queue: Array<{ slug: string; depth: number }> = [{ slug: rootSlug, depth: 0 }];
  const seen = new Set<string>();

  while (queue.length > 0) {
    const item = queue.shift()!;
    if (seen.has(item.slug)) continue;
    seen.add(item.slug);

    const sp = speciesMap[item.slug];
    if (!sp) continue;

    chain.push({
      slug: item.slug,
      name: sp.name,
      num: sp.num,
      types: sp.types || [],
      depth: item.depth,
      trigger: formatEvoTrigger(sp),
      isCurrent: item.slug === currentSlug,
    });

    if (sp.evos) {
      for (const evoName of sp.evos) {
        const evoSlug = getPokemonSlug(evoName);
        if (speciesMap[evoSlug]) {
          queue.push({ slug: evoSlug, depth: item.depth + 1 });
        }
      }
    }

    if (sp.otherFormes) {
      for (const formName of sp.otherFormes) {
        const formSlug = getPokemonSlug(formName);
        const formSp = speciesMap[formSlug];
        if (formSp && formSp.forme && formSp.forme.startsWith('Mega')) {
          queue.push({ slug: formSlug, depth: item.depth + 1 });
        }
      }
    }
  }

  return chain;
}

/**
 * Extracts and categorizes all moves for a Pokémon from BattleLearnsets and BattleMovedex.
 */
export function parseLearnset(
  slug: string,
  learnsets: Record<string, DexLearnsetEntry>,
  moves: Record<string, DexMove>
): {
  levelUp: ParsedLearnsetMove[];
  tms: ParsedLearnsetMove[];
  tutors: ParsedLearnsetMove[];
} {
  const entry = learnsets[slug];
  if (!entry || !entry.learnset) {
    return { levelUp: [], tms: [], tutors: [] };
  }

  const levelUp: ParsedLearnsetMove[] = [];
  const tms: ParsedLearnsetMove[] = [];
  const tutors: ParsedLearnsetMove[] = [];

  for (const [moveSlug, tags] of Object.entries(entry.learnset)) {
    const m = moves[moveSlug] || {
      num: 0,
      name: moveSlug,
      type: 'Normal',
      category: 'Physical',
      basePower: 0,
      accuracy: true,
      pp: 0,
    };

    for (const tag of tags) {
      if (tag.startsWith('L')) {
        const lvl = parseInt(tag.slice(1), 10);
        levelUp.push({
          slug: moveSlug,
          name: m.name,
          type: m.type,
          category: m.category,
          basePower: m.basePower,
          accuracy: m.accuracy,
          pp: m.pp,
          desc: m.shortDesc || m.desc || '',
          level: isNaN(lvl) ? 1 : lvl,
          methodDisplay: lvl === 0 ? 'Evo' : `Lv. ${lvl}`,
        });
      } else if (tag === 'M') {
        tms.push({
          slug: moveSlug,
          name: m.name,
          type: m.type,
          category: m.category,
          basePower: m.basePower,
          accuracy: m.accuracy,
          pp: m.pp,
          desc: m.shortDesc || m.desc || '',
          methodDisplay: 'TM',
        });
      } else if (tag === 'T') {
        tutors.push({
          slug: moveSlug,
          name: m.name,
          type: m.type,
          category: m.category,
          basePower: m.basePower,
          accuracy: m.accuracy,
          pp: m.pp,
          desc: m.shortDesc || m.desc || '',
          methodDisplay: 'Tutor',
        });
      }
    }
  }

  levelUp.sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name));
  tms.sort((a, b) => a.name.localeCompare(b.name));
  tutors.sort((a, b) => a.name.localeCompare(b.name));

  return { levelUp, tms, tutors };
}
